import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  useTheme,
  useMediaQuery,
  Chip,
  Divider,
  Button,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
} from "@mui/material";
import UserLayout from "components/layouts/UserLayout";
import CustomTextField from "components/mui/text-field";
import { useNavigate } from "react-router-dom";
import Icon from "components/icon";
import "assets/CSS/Switch.css";

const TaxCodeMapping = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
    const countyTaxes = [
    { county: "Los Angeles County", tax: "8.0%" },
    { county: "San Diego County", tax: "7.25%" },
    { county: "Santa Clara County", tax: "8.25%" },
    { county: "Miami-Dade County, FL", tax: "7.0%" },
    { county: "Dallas County, TX", tax: "8.25%" },
    { county: "Clark County, NV", tax: "8.375%" },
    { county: "Fulton County, GA", tax: "8.9%" },
  ];

  const [selectedCategory, setSelectedCategory] = useState("");

  const handleChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  // Summary cards data
  const summaryCardsData = [
    {
      id: 1,
      value: "12",
      label: "Total Tax Codes",
      icon: "tabler:receipt-tax",
      iconColor: "#3D3D3D",
      bgColor: "#7C8DB51F",
    },
    {
      id: 2,
      value: "9.25%",
      label: "Avg. Tax Rate",
      icon: "solar:dollar-linear",
      iconColor: "#003A70",
      bgColor: "#f0f0f1",
    },
    {
      id: 3,
      value: "10",
      label: "Mapped",
      icon: "mdi:check-all",
      iconColor: "#23A26D",
      bgColor: "#E8F5E9",
    },
    {
      id: 4,
      value: "3",
      label: "Pending Items",
      icon: "material-symbols:pending-outline",
      iconColor: "#D80027",
      bgColor: "#FFEBEE",
    },
  ];

  // Tax codes data
  const taxCodes = [
    {
      id: 1,
      name: "Federal Excise Tax",
      taxType: "Excise Tax",
      rate: "0.50%",
      status: "pending",
      qbCode: "CA-STATE",
      clientMapping: "Not mapped",
      lastSync: "Never",
      applicableTo: ["Fuel", "Equipment"],
    },
    {
      id: 2,
      name: "California State Tax",
      taxType: "Sales Tax",
      rate: "7.25%",
      status: "mapped",
      qbCode: "CA-STATE",
      clientMapping: "State Sales Tax",
      lastSync: "2 hours ago",
      applicableTo: ["Materials", "Equipment"],
    },
  ];

  // Tax application rules data
  const taxRules = [
    {
      category: "California State Tax:",
      rate: "7.25%",
    },
    {
      category: "Local Tax (L.A):",
      rate: "2.00%",
    },
    {
      category: "Total Tax Rate:",
      rate: "9.25%",
      isTotal: true,
    },
  ];

  const serviceTaxRules = [
    {
      service: "Labor Services:",
      status: "Tax Exempt",
    },
    {
      service: "Professional Services:",
      status: "Tax Exempt",
    },
  ];

  // Status chip styling
  const getStatusConfig = (status) => {
    const statusConfig = {
      mapped: {
        color: "#E8F5E9",
        textColor: "#23A26D",
        label: "Mapped",
        icon: "heroicons:check-circle",
      },
      pending: {
        color: "#FFF3E0",
        textColor: "#DD992F",
        label: "Pending",
        icon: "heroicons:clock",
      },
      failed: {
        color: "#FFEBEE",
        textColor: "#D80027",
        label: "Sync Failed",
        icon: "heroicons:x-circle",
      },
    };

    return statusConfig[status] || statusConfig["pending"];
  };

  const getStatusChip = (status) => {
    const config = getStatusConfig(status);

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          backgroundColor: config.color,
          color: config.textColor,
          fontWeight: 600,
          fontSize: "0.75rem",
          height: 24,
          minWidth: 80,
        }}
      />
    );
  };

  const handlePageBack = () => {
    navigate("/quickbooks");
  };

  return (
    <UserLayout
      contentHeightFixed={false}
      NavURL={handlePageBack}
      showIcon={true}
      activeLink="Quickbooks/Taxes"
    >
      <Box sx={{ p: { xs: 1, md: 1 }, mx: "auto" }}>
        <Card
          sx={{
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-start",
                mb: 4,
              }}
            >
              <Button
                variant="contained"
                color="main"
                size="medium"
                startIcon={
                  <Icon icon="heroicons:arrow-path" width={20} height={20} />
                }
              >
                Sync Now
              </Button>
            </Box>

            {/* Summary Cards Section */}
            <Box sx={{ mb: 4 }}>
              <Grid container spacing={3}>
                {summaryCardsData.map((card) => (
                  <Grid item xs={12} sm={6} md={3} key={card.id}>
                    <Box
                      sx={{
                        backgroundColor: "background.paper",
                        p: 4,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        minHeight: 120,
                      }}
                    >
                      {/* Icon on the left */}
                      <Box
                        sx={{
                          display: "inline-flex",
                          backgroundColor: card.bgColor,
                          borderRadius: "12px",
                          width: 48,
                          height: 48,
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon
                          icon={card.icon}
                          width={24}
                          height={24}
                          style={{ color: card.iconColor }}
                        />
                      </Box>

                      <Box sx={{ textAlign: "right", flex: 1, ml: 2 }}>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
                        >
                          {card.value}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {card.label}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Tax Codes List */}
            <Box sx={{ mb: 4 }}>
              <Card
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                  p: 3,
                }}
              >
                {/* Search and Filters */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 4,
                  }}
                >
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      Tax Code Mapping
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      QuickBooks tax codes automatically import and map to
                      Client tax categories
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <CustomTextField
                      placeholder="Search"
                      size="small"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{
                        width: 200,
                        "& .MuiOutlinedInput-root": {
                          height: "30px",
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Icon icon="tabler:search" />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <Typography variant="body1" color="text.secondary">
                            All
                          </Typography>
                        </MenuItem>
                        <MenuItem value="mapped">Mapped</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                {/* Tax Codes List */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {taxCodes.map((tax, index) => (
                    <Card
                      key={tax.id}
                      sx={{
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                        overflow: "visible",
                      }}
                    >
                      <CardContent sx={{ p: 0 }}>
                        {/* Tax Header */}
                        <Box
                          sx={{
                            p: 4,
                            pb: 2,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                              pt: 2,
                            }}
                          >
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                              {tax.name}
                            </Typography>
                            <Box
                              sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                px: 1.5,
                                py: 0.5,
                                backgroundColor: "background.paper",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: "text.secondary",
                                }}
                              >
                                {tax.taxType}
                              </Typography>
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              pt: 2,
                            }}
                          >
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: 600,
                                color: "#4374A1",
                              }}
                            >
                              {tax.rate}
                            </Typography>
                            {getStatusChip(tax.status)}
                          </Box>
                        </Box>

                        {/* Tax Details */}
                        <Box sx={{ p: 3 }}>
                          {/* QB Code, Client Mapping, Last Sync in single row box */}
                          <Card
                            sx={{
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: "#003A700D",
                              backgroundColor: "#003A700D",
                              p: 4,
                              mb: 2,
                            }}
                          >
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} md={3}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                  }}
                                >
                                  <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{ mb: 0.5 }}
                                  >
                                    Code:
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontWeight: 530,
                                    }}
                                  >
                                    {tax.qbCode}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                  }}
                                >
                                  <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{ mb: 0.5 }}
                                  >
                                    Cleri Mapping:
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontWeight: 530,
                                    }}
                                  >
                                    {tax.clientMapping}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                  }}
                                >
                                  <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{ mb: 0.5 }}
                                  >
                                    Last Sync:
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 530 }}
                                  >
                                    {tax.lastSync}
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </Card>

                          {/* Applicable to and Mapping boxes side by side */}
                          <Grid container spacing={2}>
                            {/* Applicable to box */}
                            <Grid
                              item
                              xs={12}
                              md={tax.status !== "mapped" ? 6 : 6}
                            >
                              <Card
                                sx={{
                                  borderRadius: 2,
                                  border: "1px solid",
                                  borderColor: "#97A4B10D",
                                  backgroundColor: "#97A4B10D",
                                  p: 4,
                                  height: "100%",
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{ mb: 2, fontWeight: 600 }}
                                >
                                  Applicable to:
                                </Typography>

                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                  }}
                                >
                                  {tax.applicableTo.map((item, idx) => (
                                    <Chip
                                      key={idx}
                                      label={item}
                                      size="small"
                                      sx={{
                                        backgroundColor: "#E8F5E9",
                                        color: "#23A26D",
                                        border: "1px solid",
                                        borderColor: "#E8F5E9",
                                        fontWeight: 500,
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Card>
                            </Grid>

                            {/* Mapping box - only for non-mapped items */}
                            {tax.status !== "mapped" && (
                              <Grid item xs={12} md={6}>
                                 <Card
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "#DD992F80",
        backgroundColor: "#DD992F0D",
        p: 3,
        height: "100%",
      }}
    >
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Map to Cleri Tax Category
      </Typography>

      <FormControl size="small" fullWidth>
        <Select
          displayEmpty
          value={selectedCategory}
          onChange={handleChange}
          sx={{
            backgroundColor: "background.paper",
            "& .MuiSelect-select": {
              py: 1,
              height: "33px",
              display: "flex",
              alignItems: "center",
            },
          }}
        >
          <MenuItem value="">
            <Typography variant="body2" color="text.secondary">
              Select County and Tax...
            </Typography>
          </MenuItem>

          {countyTaxes.map((item) => (
            <MenuItem key={item.county} value={item.county}>
              {item.county} — {item.tax}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Card>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Card>
            </Box>

            {/* Tax Application Rules - Below all tax records */}
            <Card
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
                p: 4,
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                Tax Application Rules
              </Typography>

              <Typography variant="body1" sx={{ mb: 4 }}>
                Taxes are applied in Cleri based on QuickBooks settings
              </Typography>

              {/* Two boxes side by side */}
              <Grid container spacing={3}>
                {/* First Box - Tax Rates */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "background.paper",
                      p: 3,
                      height: "100%",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Tax Application Rules
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Physical items are subject to sales tax based on customer
                      location and item type.
                    </Typography>

                    {/* Tax Rates Table */}
                    <Box>
                      {taxRules.slice(0, -1).map((rule, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "text.secondary",
                            }}
                          >
                            {rule.category}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "text.primary",
                            }}
                          >
                            {rule.rate}
                          </Typography>
                        </Box>
                      ))}

                      {/* Total Tax Rate for Tax Rates Box */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          py: 1.5,
                          borderTop: "1px solid",
                          borderColor: "divider",
                          px: 2,
                          borderRadius: 1,
                          mt: 1,
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 700,
                            color: "#23A26D",
                          }}
                        >
                          Total Tax Rate
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 700,
                            color: "#23A26D",
                          }}
                        >
                          9.25%
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>

                {/* Second Box - Services */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "background.paper",
                      p: 3,
                      height: "100%",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Services
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Most construction services are exempt from sales tax in
                      California.
                    </Typography>

                    {/* Service Tax Rules */}
                    <Box>
                      {serviceTaxRules.map((service, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "text.secondary",
                            }}
                          >
                            {service.service}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "text.secondary",
                            }}
                          >
                            {service.status}
                          </Typography>
                        </Box>
                      ))}

                      {/* Total Tax Rate for Services Box */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          py: 1.5,
                          borderTop: "1px solid",
                          borderColor: "divider",
                          px: 2,
                          borderRadius: 1,
                          mt: 2,
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 700,
                            color: "#23A26D",
                          }}
                        >
                          Total Tax Rate
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 700,
                            color: "#23A26D",
                          }}
                        >
                          9.25%
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </Card>
          </CardContent>
        </Card>
      </Box>
    </UserLayout>
  );
};

export default TaxCodeMapping;
